/// <reference types="vite-plugin-svgr/client" />

import { useState, ReactNode } from 'react';
import { compact } from 'lodash';
import { twMerge } from 'tailwind-merge';
import { classNames, formatCurrency, useTabletWidth } from '../../utils/index';
import * as Tooltip from '@radix-ui/react-tooltip';
import { ReactComponent as TooltipIcon } from '../icons/tooltip.svg';
import { ReactComponent as AccordionArrow } from '../icons/accordion-arrow.svg';
import {
  BadgeType,
  InteractiveRatesRateFragmentFragment,
  LineItemFragmentFragment,
  RateRowBadge,
  ToolTipFragmentFragment,
  ToolTipItemFragmentFragment,
  TotalCostItemFragmentFragment,
  TransactionPurposeTab,
} from './types';
import { LabelTooltip } from '../LabelTooltip';
import NoRateResults from '../../images/NoRateResults.svg';

function RatesDefaultEmptyState() {
  return (
    <div className="mt-8 flex flex-col items-center justify-center">
      <div className="flex gap-[24px] w-auto md:w-[495px] flex-col items-center justify-center mt-[12px]">
        <img src={NoRateResults} className="h-[70px] w-[62px] text-red-300" />
        <p className="typo-body font-bold text-[20px] text-center">
          We can&apos;t find any rates. Try changing your details
          <br />
          above, or create an account to explore more options.
        </p>
      </div>
    </div>
  );
}

const tooltipStyles =
  'data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade text-violet11  select-none rounded-[4px] bg-white px-[15px] py-[10px] text-[15px] leading-none shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] will-change-[transform,opacity]';

export const BadgeTooltip: React.FC<{ badge: RateRowBadge }> = ({ badge }) => {
  const isMobile = useTabletWidth();
  const getMobileBadgeText = () => {
    if (badge.badgeType === 'FIXED' && isMobile) {
      const split = badge.label.split('YEAR');
      const label = split.join('YR');
      return label;
    } else {
      return badge.label;
    }
  };
  const badgeText = getMobileBadgeText();

  return (
    <div key={badge?.badgeType}>
      {badge?.badgeType === 'FHA' || badge?.badgeType === 'HOMEREADY' ? (
        <div
          className={classNames(
            'items-center rounded inline-flex justify-center pt-[3px] pr-[4px] pb-[1px] pl-[6px] h-4 mr-1 typo-eyebrow',
            badgeClasses(badge?.badgeType)
          )}
        >
          {badge?.badgeType === 'FHA' ? 'FHA' : 'HOMEREADY'}
          <LabelTooltip
            tooltipClassName={'typo-body-small max-w-[300px] text-left'}
            iconClassName={classNames(
              'text-[12px] text-sunroom-800 mb-[1px] ml-[2px] cursor-pointer',
              badgeClasses(badge?.badgeType)
            )}
          >
            {badge?.tooltip}
          </LabelTooltip>
        </div>
      ) : (
        <div
          className={classNames(
            'mr-[4px] items-center rounded inline-flex justify-center h-[1rem]',
            badgeClasses(badge?.badgeType)
          )}
          style={{ padding: '3px 4px 1px 6px' }}
        >
          <div
            className={classNames(
              'typo-eyebrow whitespace-nowrap',
              badgeClasses(badge?.badgeType)
            )}
          >
            {badgeText}
          </div>
          <LabelTooltip
            tooltipClassName={'typo-body-small max-w-[300px] text-left'}
            iconClassName={classNames(
              'text-[12px] mb-[1px] ml-[2px] cursor-pointer',
              badgeClasses(badge?.badgeType)
            )}
          >
            <>{badge?.tooltip}</>
          </LabelTooltip>
        </div>
      )}
    </div>
  );
};

function InfoTooltip({ children }: { children: ReactNode }) {
  return (
    <Tooltip.Provider delayDuration={100}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span className="cursor-pointer">
            <TooltipIcon className="ml-1 mt-[-1px] h-[10px] w-[10px]" />
          </span>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className={classNames(tooltipStyles, 'min-w-[340px] max-w-[340px]')}
            sideOffset={5}
          >
            <Tooltip.Arrow className="fill-white" />
            {children}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
function LineItemDisplay({
  item,
  className,
}: {
  className?: string;
  item: LineItemFragmentFragment;
}) {
  return (
    <div className={classNames('flex justify-between py-[3px]', className)}>
      <div className="typo-body-small text-sidewalk-600 flex items-center">
        {item.label}
      </div>
      <div className="typo-body-small text-sidewalk-600">{item.value}</div>
    </div>
  );
}

function TooltipItemDisplay({ item }: { item: ToolTipItemFragmentFragment }) {
  return (
    <div className="flex justify-between py-[3px]">
      <div
        className={classNames(
          'typo-body-small text-sidewalk-600 flex items-center',
          item.isGreen ? 'text-pool-500' : ''
        )}
      >
        {item.label}
      </div>
      <div
        className={classNames(
          'typo-body-small text-sidewalk-600',
          item.isGreen ? 'text-pool-500 bg-pool-100 rounded-full px-2' : ''
        )}
      >
        {item.value}
      </div>
    </div>
  );
}

function LineItemTooltip({
  tooltip,
  children,
}: {
  tooltip: ToolTipFragmentFragment;
  children?: ReactNode;
}) {
  return (
    <InfoTooltip>
      {children}{' '}
      {compact(tooltip.items)?.map((item, idx) => {
        return <TooltipItemDisplay key={idx} item={item} />;
      })}
    </InfoTooltip>
  );
}

function badgeClasses(badgeType: BadgeType) {
  switch (badgeType) {
    case BadgeType.Fha:
      return 'bg-fireplace-200 text-fireplace-600';
    case BadgeType.Homeready:
      return 'bg-sunroom-100 text-sunroom-800';
    case BadgeType.Fixed:
      return 'bg-yard-200 text-yard-700';
    case BadgeType.Conventional:
      return 'bg-pool-100 text-pool-600';
    case BadgeType.Jumbo:
      return 'bg-[#FAE4FF] text-[#A94ABF]';
    case BadgeType.Usda:
      return 'bg-[#D4F1FA] text-[#58818E]';
    case BadgeType.Va:
      return 'bg-[#E5D8FF] text-[#745DA3]';
    case BadgeType.Arm:
    default:
      return 'bg-morton-200 text-morton-700';
  }
}

export const RateRowExpandedBlock: React.FC<{
  title: string;
  data: TotalCostItemFragmentFragment;
}> = ({ title, data }) => {
  return (
    <div className="__rate-row-expanded-block border-hardwood-200 flex-[1] rounded-[4px] border-[1px] px-[20px] py-[16px]">
      <div className="typo-eyebrow text-morton-600 pb-[3px]">{title}</div>
      {compact(data.components).map((item) => {
        let tooltip: ToolTipFragmentFragment | undefined | null;

        if (item.__typename === 'LineItem') {
          tooltip = item.tooltip;
        }
        return (
          <div key={item.label} className="flex justify-between py-[3px]">
            <div className="typo-body-small text-sidewalk-600 flex items-center">
              {item.label}{' '}
              {item.__typename === 'LineItem' && item.tooltip ? (
                <LineItemTooltip tooltip={item.tooltip}>
                  <LineItemDisplay className="font-bold" item={item} />
                </LineItemTooltip>
              ) : null}
            </div>
            <div className="typo-body-small text-sidewalk-600">
              {item.value}
            </div>
          </div>
        );
      })}

      <div className="border-t-hardwood-200 mt-2 flex justify-between border-t-[1px] pt-2">
        <div className="typo-body-small text-sidewalk-600 font-bold">
          {data.label}
        </div>
        <div className="typo-body-small text-sidewalk-600 font-bold">
          {data.value}
        </div>
      </div>
    </div>
  );
};

const LoanCollapsedBody: React.FC<{
  monthlyCostsData: TotalCostItemFragmentFragment;
  closingCostsData: TotalCostItemFragmentFragment;
  isUpdating: boolean;
}> = ({ monthlyCostsData, closingCostsData, isUpdating }) => {
  const isTabletWidth = useTabletWidth();
  const CostItemRow: React.FC<{ costItem: any }> = ({ costItem }) => {
    return (
      <>
        <div className="flex items-center">
          <div className="typo-body-small sm:typo-body text-sidewalk-900 whitespace-nowrap">
            {costItem.label === 'Property taxes & building fees' ? (
              <div className="typo-body text-sidewalk-900 w-full">
                <span className="typo-body-small sm:typo-body text-sidewalk-900">
                  {isTabletWidth ? (
                    'Property taxes'
                  ) : (
                    <>Property taxes &amp; building fees</>
                  )}
                </span>
              </div>
            ) : (
              costItem.label
            )}
          </div>
          <div>
            {costItem.tooltip ? (
              <LabelTooltip
                iconClassName="text-sidewalk-200 hover:text-hardwood-400"
                tooltipClassName="max-w-[300px]"
              >
                {costItem.tooltip.description ? (
                  costItem.tooltip.description
                ) : !!costItem.tooltip.items.length ? (
                  <div className="flex flex-col w-full">
                    <div className="grid grid-cols-2 gap-1 justify-between">
                      <div className="typo-body-small sm:typo-body  text-sidewalk-600 font-bold whitespace-nowrap">
                        {costItem.label}
                      </div>
                      <div className="typo-body-small sm:typo-body  text-sidewalk-600 font-bold self-end text-end">
                        {costItem.value}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-1 justify-between">
                      {costItem.tooltip.items.map(
                        (item: {
                          label: string;
                          value: string;
                          isGreen?: boolean;
                        }) => {
                          return (
                            <>
                              <div
                                className={classNames(
                                  'typo-body-small whitespace-nowrap',
                                  !!item.isGreen
                                    ? 'text-pool-500'
                                    : 'text-sidewalk-600'
                                )}
                              >
                                {item.label}
                              </div>
                              <div
                                className={classNames(
                                  'typo-body-small self-end text-end',
                                  !!item.isGreen
                                    ? 'text-pool-500'
                                    : 'text-sidewalk-600'
                                )}
                              >
                                <span
                                  className={classNames(
                                    !!item.isGreen
                                      ? 'bg-pool-100 rounded-lg gap-2 px-1'
                                      : ''
                                  )}
                                >
                                  {item.value}
                                </span>
                              </div>
                            </>
                          );
                        }
                      )}
                    </div>
                  </div>
                ) : null}
              </LabelTooltip>
            ) : null}
          </div>
        </div>
        <div className="typo-body-small sm:typo-body text-sidewalk-600 text-end">
          {costItem.value}
        </div>
      </>
    );
  };

  return (
    // <div className="w-full grid min-[767px]:grid-cols-1 min-[1200px]:grid-cols-10 min-[767px]:gap-4 min-[1200px]:gap-0 ">
    <div className="w-full grid min-[640px]:grid-cols-1 min-[767px]:grid-cols-10 min-[767px]:gap-4 min-[1200px]:gap-0 ">
      {/* <div className="col-span-5 border-[1px] border-sidewalk-50 rounded py-4 px-5 mb-6 lg:mb-0 min-[767px]:mr-0 min-[1200px]:mr-6"> */}
      <div className="col-span-5 border-[1px] border-sidewalk-50 rounded py-4 px-5 mb-[16px] min-[640px]:mb-6 min-[767px]:mb-0 min-[767px]:mr-0 min-[1200px]:mr-6">
        <div className="typo-eyebrow text-morton-600 mb-3 text-left">
          Monthly costs
        </div>
        <div className="w-full grid grid-cols-[repeat(2,minmax(20px,100px))] min-[376px]:grid-cols-[1fr_1fr] gap-2">
          {monthlyCostsData.components.map((component, i) => {
            if (component) {
              return (
                <CostItemRow
                  costItem={component}
                  key={`${component.label}-${i}`}
                />
              );
            } else {
              return <></>;
            }
          })}
        </div>
        <div className="w-full flex justify-between border-t-[1px] border-sidewalk-50 pt-3 mt-3">
          <div className="typo-body font-bold text-sidewalk-600">
            {monthlyCostsData.label}
          </div>
          <div className="typo-body font-bold text-sidewalk-600">
            {monthlyCostsData.value}
          </div>
        </div>
      </div>
      <div className="col-span-5 border-[1px] border-sidewalk-50 rounded py-4 px-5">
        <div className="typo-eyebrow text-morton-600 mb-3 text-left">
          Cash to Close
        </div>
        <div className="grid grid-cols-[repeat(2,minmax(20px,100px))] min-[376px]:grid-cols-[1fr_1fr] gap-2">
          {closingCostsData.components.map((component, i) => {
            if (component) {
              return (
                <CostItemRow
                  costItem={component}
                  key={`${component.label}-${i}`}
                />
              );
            } else {
              return <></>;
            }
          })}
        </div>
        <div className="w-full flex justify-between border-t-[1px] border-sidewalk-50 pt-3 mt-3">
          <div className="typo-body font-bold text-sidewalk-600">
            {closingCostsData.label === 'Cash back at closing' ? (
              <div className="flex items-center">
                Cash back at closing
                <LabelTooltip
                  tooltipClassName="max-w-[300px]"
                  iconClassName="text-sidewalk-300"
                >
                  Rather than paying anything out of pocket at closing, you’ll
                  receive this amount.
                </LabelTooltip>
              </div>
            ) : (
              closingCostsData.label
            )}
          </div>
          <div className="typo-body font-bold text-sidewalk-600">
            {closingCostsData.value}
          </div>
        </div>
      </div>
    </div>
  );
};

export function RatesToolDisplayContainer({
  rates,
  selectedPurposeTab,
  queryIsLoading,
  ctaLink,
  isHelocHeloanOrOther,
}: {
  rates?: InteractiveRatesRateFragmentFragment[];
  selectedPurposeTab: TransactionPurposeTab;
  queryIsLoading: boolean;
  ctaLink: string;
  isHelocHeloanOrOther: boolean;
}) {
  if (isHelocHeloanOrOther) {
    return null;
  }

  if (!queryIsLoading && !rates?.length) {
    return <RatesDefaultEmptyState />;
  } else {
    return (
      <>
        {rates?.length
          ? rates.map((rate, idx) => {
              return (
                <RatesToolRateDisplay
                  key={`${idx}~${rate.adjustedPrice}-${rate.interestRate}`}
                  index={idx}
                  rate={rate}
                  selectedPurposeTab={selectedPurposeTab}
                  expandedCTA={
                    <>
                      <div className="typo-body-small my-[20px] w-full text-center text-sidewalk-900">
                        Create an account to download a quote or lock in your
                        rate:
                      </div>

                      <div className="items- flex justify-center">
                        <a
                          href={ctaLink}
                          className="__refine-rate-button center typo-button center inline-flex h-[40px] cursor-pointer select-none items-center justify-center gap-2 rounded-[80px] border-[1px] border-solid border-morton-600 bg-morton-600 px-[20px] text-white"
                        >
                          GET STARTED
                        </a>
                      </div>
                    </>
                  }
                />
              );
            })
          : null}
      </>
    );
  }
}

export function RatesToolRateDisplay({
  rate,
  defaultExpanded = false,
  expandedCTA,
  index,
  selectedPurposeTab,
}: {
  expandedCTA?: ReactNode;
  rate: InteractiveRatesRateFragmentFragment;
  defaultExpanded?: boolean;
  index?: number;
  selectedPurposeTab: TransactionPurposeTab;
}) {
  const isPurchase = selectedPurposeTab === TransactionPurposeTab.Purchase;
  const isTabletWidth = useTabletWidth();
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const {
    badges,
    interestRate,
    apr,
    monthlyPi,
    totalLoanCostsNew,
    monthlyCostItems,
    cashToCloseCostItems,
    totalCashToClose,
  } = rate;

  const totalCashToCloseLabel =
    totalCashToClose < 0 ? 'Cash to you' : 'Cash to close';

  if (isTabletWidth) {
    return (
      <div
        className={classNames(
          'mt-4 relative w-full rounded-[4px] bg-white shadow-lg transition-all',
          expanded ? 'h-auto' : 'h-[196px]'
        )}
      >
        <div className="p-[20px] pb-0" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center">
            <div className="flex-1">
              <div className="flex flex-col justify-center items-start">
                <div className="typo-body text-sidewalk-500 text-[13px]">
                  Interest Rate
                </div>
                <div className="typo-body text-sidewalk-900 mt-2 text-[24px]">
                  <span className="font-bold">{interestRate.toFixed(3)}%</span>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-col justify-center items-start">
                <div className="typo-body text-sidewalk-500 text-[13px]">
                  APR
                </div>
                <div className="typo-body text-sidewalk-900 mt-2 text-[24px]">
                  <span>{apr.toFixed(3)}%</span>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <div className="flex-1">
              <div className="flex flex-col justify-center items-start">
                <div className="typo-body-small text-sidewalk-500 text-[13px]">
                  Principal &amp; interest
                </div>
                <div className="typo-body text-sidewalk-900 mt-1 text-[20px]">
                  {formatCurrency(monthlyPi, { maximumFractionDigits: 0 })}/mo
                </div>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex flex-col justify-center items-start">
                <div className="typo-body-small text-sidewalk-500 text-[13px]">
                  {isPurchase ? 'Est. Loan Costs' : totalCashToCloseLabel}
                </div>
                <div className="typo-body text-sidewalk-900 mt-1 text-[20px]">
                  {formatCurrency(
                    isPurchase ? totalLoanCostsNew : Math.abs(totalCashToClose),
                    {
                      maximumFractionDigits: 0,
                    }
                  )}
                </div>
              </div>
            </div>
          </div>

          <div
            className="absolute top-4 right-4 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            <AccordionArrow
              className={'text-hardwood-700'}
              style={{
                transform: expanded ? '' : 'rotate(180deg)',
              }}
              fontSize={24}
            />
          </div>
          <div className="flex items-center sm:items-start h-[40px] mt-2">
            <div className="flex flex-1 flex-wrap justify-start items-start">
              {compact(badges).map((badge) => (
                <BadgeTooltip badge={badge} />
              ))}
            </div>
          </div>
        </div>

        {expanded ? (
          <div className="px-2 py-[20px] sm:p-[20px]">
            <div className="__rate-row-expanded">
              <LoanCollapsedBody
                monthlyCostsData={monthlyCostItems}
                closingCostsData={cashToCloseCostItems}
                isUpdating={false}
              />
              <div
                className="absolute top-4 right-4 cursor-pointer "
                onClick={() => setExpanded(true)}
              ></div>
            </div>
            <div
              className="cursor-pointer typo-body text-morton-600 flex justify-end mt-4"
              onClick={() => setExpanded(false)}
            >
              Close details
              <AccordionArrow
                className={'text-morton-600 rotate(180deg)'}
                fontSize={24}
              />
            </div>
            {expandedCTA}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        'relative w-full rounded-[4px] bg-white max-[1199px]:p-[32px_24px_24px] min-[1200px]:p-[32px] shadow-lg transition-all',
        expanded ? 'h-auto' : 'h-fit',
        index == 0 ? '' : 'mt-4'
      )}
    >
      <div className="__rate-row-top-line align-center flex h-auto w-full justify-between">
        <div
          className={classNames(
            'w-[133px] items-start flex flex-col-reverse justify-start min-[1200px]:justify-center max-[1199px]:absolute max-[1199px]:flex-row max-[1199px]:top-[6px] min-[1200px]:relative min-[1200px]:flex-col-reverse'
          )}
        >
          {compact(badges).map((badge) => (
            <BadgeTooltip badge={badge} />
          ))}
        </div>

        <div className="flex flex-col justify-center items-start w-[80px] text-left">
          <div className="typo-body text-[19px] text-sidewalk-900">
            <span className="font-bold">{interestRate.toFixed(3)}%</span>
          </div>
          <div className="typo-body text-sidewalk-500 text-[13px]">
            Interest Rate
          </div>
        </div>
        <div className="flex flex-col justify-center items-start  w-[80px] text-left">
          <div className="typo-body text-[19px] text-sidewalk-900">
            <span>{apr.toFixed(3)}%</span>
          </div>
          <div className="typo-body text-sidewalk-500 text-[13px]">APR</div>
        </div>
        <div className="flex flex-col items-start justify-center w-[100px]">
          <div className="typo-body text-[19px] text-sidewalk-900">
            {formatCurrency(monthlyPi, { maximumFractionDigits: 0 })}/mo
          </div>
          <div className="typo-body text-sidewalk-500 text-[13px] whitespace-nowrap">
            Principal &amp; interest
          </div>
        </div>
        <div className="flex flex-col justify-center items-start w-[100px] pl-[8px]">
          <div className="typo-body text-[19px] text-sidewalk-900">
            {formatCurrency(
              isPurchase ? totalLoanCostsNew : Math.abs(totalCashToClose),
              {
                maximumFractionDigits: 0,
              }
            )}
          </div>
          <div className="typo-body text-sidewalk-500 text-[13px]">
            {isPurchase ? 'Est. Loan Costs' : totalCashToCloseLabel}
          </div>
        </div>

        <div className="flex w-[120px] flex-col justify-center">
          <div
            onClick={() => setExpanded(!expanded)}
            className="__show-details-button typo-button border-morton-600 text-morton-600 flex h-[40px] cursor-pointer select-none items-center justify-center rounded-[80px] border-[1px] border-solid px-[20px] "
          >
            {expanded ? 'CLOSE' : 'DETAILS'}
          </div>
        </div>
      </div>
      {expanded ? (
        <div className="__rate-row-expanded">
          <div className="__rate-row-expanded-blocks flex justify-around gap-[24px] mt-6">
            <LoanCollapsedBody
              monthlyCostsData={monthlyCostItems}
              closingCostsData={cashToCloseCostItems}
              isUpdating={false}
            />
          </div>

          {expandedCTA}
        </div>
      ) : null}
    </div>
  );
}
